import {
  BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM,
  BRANCH_ENVIRONMENT_PRODUCTION_ENUM,
  BRANCH_ENVIRONMENT_SCRATCH_ENUM
} from '@takeshape/branches';
import { BadRequestError } from '@takeshape/errors';
import type { LayerSchema, ServiceLayers } from '@takeshape/schema';
import type { ProjectSchemaCompat, ProjectSchemaRecordType } from 'app/compat/types';
import mapValues from 'lodash/mapValues';
import { createProjectPath } from '../project/utils.js';
import type { BranchesAction } from './actions.js';
import { DEFAULT_BRANCH, SCRATCH_BRANCH } from './constants.js';
import type { Branch, BranchesState, BranchQueryArgs, LayerState, Layers, SchemaState } from './types.js';

type CurrentBranchState = Pick<BranchesState, 'currentBranch'>;

export function getBranchName(state: CurrentBranchState, action?: BranchesAction): string {
  let branch: string | undefined;
  if (action) {
    if ('meta' in action && action.meta && 'branch' in action.meta) {
      branch = action.meta.branch;
    } else if (
      'payload' in action &&
      action.payload &&
      typeof action.payload === 'object' &&
      'branch' in action.payload
    ) {
      branch = action.payload.branch;
    }
  }

  return branch ?? state.currentBranch;
}

type PathType = 'user' | 'runtime' | 'service';

function getPathType(action: BranchesAction): PathType {
  if ('meta' in action && action.meta && 'runtime' in action.meta && action.meta.runtime) {
    return 'runtime';
  }

  return 'user';
}

const pathTypeMapping = {
  user: ['layers', 'user'],
  runtime: ['runtimeSchema'],
  service: ['serviceSchema']
};

export function getSchemaPathFromAction(
  state: CurrentBranchState,
  action: BranchesAction,
  pathType?: PathType
): string[] {
  const branchName = getBranchName(state, action);
  return ['branches', branchName, ...pathTypeMapping[pathType ?? getPathType(action)]];
}

export function getSchemaPathFromState(state: CurrentBranchState, pathType: PathType): string[] {
  const branchName = getBranchName(state);
  return ['branches', branchName, ...pathTypeMapping[pathType]];
}

export type CreateBranchArgs = {
  layers?: {
    [name: string]: LayerSchema | null | undefined;
    user?: ProjectSchemaCompat | null | undefined;
  };
  runtimeSchema?: ProjectSchemaCompat;
};

function createSchemaState(schema?: ProjectSchemaCompat | null): SchemaState {
  return {
    data: schema ?? null,
    saving: false,
    loading: false
  };
}

function createLayerState<T>(schema?: T | null): LayerState<T> {
  return {
    data: schema ?? null,
    status: schema ? 'ok' : null,
    saving: false,
    loading: false
  };
}

export function createBranch({ layers, runtimeSchema }: CreateBranchArgs = {}): Branch {
  const { user, ...otherLayers } = layers ?? {};
  return {
    layers: {
      user: createLayerState(user),
      ...mapValues(otherLayers, createSchemaState)
    },
    serviceLayers: {
      loading: false
    },
    runtimeSchema: createSchemaState(runtimeSchema)
  };
}

export const summarizeApiError = (error: unknown, prefix?: string) => {
  let message: string = prefix ? `${prefix}. ` : '';
  if (error instanceof BadRequestError) {
    message += typeof error.data?.message === 'string' ? error.data.message : 'Bad Request';
    if (error.data?.errors && error.data.errors.length > 0) {
      const firstError = error.data.errors[0];
      const firstErrorMessage = `: ${firstError.message} at /${firstError.path.join('/')}`;
      if (error.data.errors.length === 1) {
        message += firstErrorMessage;
      } else {
        message += `: ${error.data.errors.length} errors. First error${firstErrorMessage}`;
      }
    }
  } else if (error instanceof Error) {
    message += error.message;
  } else if (Array.isArray(error)) {
    message += error.map((e) => summarizeApiError(e, prefix)).join('\n');
  } else if ((error as Record<string, unknown>)?.message) {
    message += String((error as Record<string, unknown>).message);
  } else {
    message += error ? String(error) : '';
  }

  return message.trim();
};

export const createBranchPath = (projectId: string, branchName = 'production') =>
  `${createProjectPath(projectId)}/branch/${encodeURIComponent(branchName)}`;

export function branchToBranchQueryArgs(projectId: string, branch: string, userId?: string): BranchQueryArgs {
  let environment: BranchQueryArgs['environment'];
  let branchName: string | undefined;

  if (branch === DEFAULT_BRANCH) {
    environment = BRANCH_ENVIRONMENT_PRODUCTION_ENUM;
  } else if (branch === SCRATCH_BRANCH) {
    environment = BRANCH_ENVIRONMENT_SCRATCH_ENUM;
  } else {
    environment = BRANCH_ENVIRONMENT_DEVELOPMENT_ENUM;
    branchName = branch;
  }

  return {
    projectId,
    environment,
    branchName,
    // TODO userId may need to become more flexible for admin ops on other scratch branches
    userId
  };
}

export function getSchemaRecordType(runtime: boolean): ProjectSchemaRecordType {
  return !runtime ? ('USER' as const) : ('RUNTIME' as const);
}

export function toServiceLayers(layers: Layers): ServiceLayers {
  const serviceLayers: ServiceLayers = {};
  for (const [serviceId, layer] of Object.entries(layers)) {
    if (serviceId !== 'user' && layer.data) {
      serviceLayers[serviceId] = {
        id: serviceId,
        schema: layer.data,
        status: 'ok'
      };
    }
  }
  return serviceLayers;
}
